// apps/radio/frontend/src/utils/querySyntax.ts
//
// TEM-1529 — which engine answers Radio's search box.
//
// Radio has ONE box. Text that uses the grammar's own syntax — a field term
// (`artist:coltrane`, `-artist:*`, `grain:track`), a field phrase (`pressed
// at: rti`, TEM-1599), `sounds like …`, or a lineage phrase — goes to
// `GET /ai/query`, where the backend parses it with the shared Rust grammar
// (compiled to wasm) and answers over Radio's own shows. Plain words stay on
// the AI search and its substring fallback, exactly as they always have.
//
// This does NOT parse. It is a copy of one yes/no question,
// `tempest_query::grammar::has_query_syntax`, and the same copy the web
// library runs (`apps/record/web/src/lib/querySyntax.ts`). A copy can drift —
// so it is held to the Rust by `vectors.json`, the shared vector file every
// surface runs (`__tests__/querySyntax.test.ts` here, `cargo test` there).
//
// A field term counts only on a name the grammar knows (`GRAMMAR_NAMES`, or
// a counted walk like `hops2`). `Jazz Tempest: Episode 102` is a show title,
// not a filter on a field called Jazz Tempest: it stays on the text search
// that always found it. The name list is pinned in `vectors.json`.

interface Token {
  /** Quotes removed, escapes resolved. */
  text: string;
  /** The whole token was one quoted string: free text, never an operator. */
  fullyQuoted: boolean;
}

/** `grammar::tokenize`, line for line. */
function tokenize(input: string): Token[] {
  const out: Token[] = [];
  let cur = '';
  let inQuote = false;
  let startedQuoted = false;
  let sawAny = false;
  let escape = false;
  for (const ch of input) {
    if (escape) {
      cur += ch;
      escape = false;
      sawAny = true;
      continue;
    }
    if (ch === '\\' && inQuote) {
      escape = true;
    } else if (ch === '"') {
      if (!inQuote && cur === '') startedQuoted = true;
      inQuote = !inQuote;
      sawAny = true;
    } else if (/\s/.test(ch) && !inQuote) {
      if (sawAny) out.push({ text: cur, fullyQuoted: startedQuoted });
      cur = '';
      startedQuoted = false;
      sawAny = false;
    } else {
      cur += ch;
      sawAny = true;
    }
  }
  if (sawAny) out.push({ text: cur, fullyQuoted: startedQuoted });
  return out;
}

/** `grammar::split_field_term`'s name: an identifier (letter or `_` first),
 *  then immediately an operator. */
const FIELD_TERM = /^([A-Za-z_][A-Za-z0-9_]*)(?:>=|<=|!=|==|:|=|>|<|~|\^)/;

/** Every name the grammar reads as a field, lower case — `grammar::grammar_names`:
 *  the columns and their aliases, `like`, `grain`, `sort`, `limit`, the lineage
 *  names. Held to the Rust list by `grammar_names` in `vectors.json`. */
export const GRAMMAR_NAMES: ReadonlySet<string> = new Set([
  'added', 'ai_confidence', 'album', 'arm', 'artist', 'artwork', 'audio_energy',
  'barcode', 'base_grade', 'bit_rate', 'bitrate', 'bpm', 'cat', 'catno',
  'click_density', 'clipping', 'codec', 'community_have', 'community_rating',
  'community_want', 'companies', 'company', 'composer', 'condition', 'country',
  'credit', 'credited', 'credits', 'crest_factor', 'cut_at', 'cut_by', 'cutter',
  'dance', 'danceability', 'date_added', 'discogs_notes', 'dominant_color',
  'duration', 'dynamic_range', 'energy', 'engineer', 'engineered_by', 'era',
  'family', 'file_format', 'for_sale', 'format', 'format_details',
  'format_text', 'genre', 'grade', 'grain', 'have', 'hiss', 'hiss_db', 'hops',
  'inner_groove_thd', 'instrument', 'instruments', 'key', 'label',
  'lacquer_cut_at', 'last_played', 'last_played_at', 'length', 'like', 'limit',
  'listeners_also',
  'loudness', 'loudness_lufs', 'loudness_range', 'lowest_price', 'lra', 'lufs',
  'lyricist', 'mastered', 'mastered_at', 'mastered_by', 'mastering_studio',
  'matrix', 'mbid', 'member_of', 'members', 'mixed_at', 'mood', 'mood2',
  'mood_secondary', 'musicbrainz', 'musicbrainz_id', 'name', 'noise_floor',
  'noise_floor_db', 'num_for_sale', 'original_year', 'personnel', 'plated_at',
  'play_count', 'playable', 'plays', 'pressed', 'pressed_by', 'pressing_era',
  'pressing_family', 'price', 'price_change', 'price_change_30d', 'producer',
  'purchase_date', 'purchase_location', 'purchase_price', 'rating',
  'rating_count', 'recorded_at', 'recorded_at_facility', 'release_notes',
  'remixed_by', 'remixer', 'riddim', 'rights_society', 'rumble', 'rumble_db',
  'runout', 'sample_rate', 'series', 'shares_personnel', 'singer', 'sleeve',
  'song', 'songwriter', 'sort', 'sounds_like', 'source', 'spectral_centroid',
  'stereo_width', 'style', 'styles', 'tagged', 'tags', 'tempo', 'thd', 'title',
  'tonal_balance', 'track', 'track_number', 'true_peak', 'upc', 'user_rating',
  'user_tags', 'vibe', 'vibe_tags', 'vinyl_grade', 'vocalist', 'vocals',
  'votes', 'want', 'wants', 'work', 'writer', 'year', 'year_added',
]);

/** Every field phrase, lower case — `grammar::grammar_phrases`: `pressed at`,
 *  `recorded at`. A phrase names a field only with an operator straight after
 *  its last word (`pressed at: rti`); without one the words are just words.
 *  Held to the Rust list by `grammar_phrases` in `vectors.json` (TEM-1599). */
export const GRAMMAR_PHRASES: readonly string[] = [
  'artwork by', 'composed by', 'cut at', 'cut by', 'lacquer cut at',
  'lacquer cut by', 'listeners also', 'mastered at', 'mastered by', 'member of', 'members of',
  'mixed at', 'plated at', 'pressed at', 'pressed by', 'produced by',
  'recorded at', 'remixed by', 'sleeve by', 'sung by', 'versions of',
  'vocals by', 'written by',
];

/** A counted walk, `hops2:` — a name by `grammar::lineage_field`'s rule. */
const COUNTED_HOPS = /^hops[0-9]+$/;

/** The field name a token spells, `-` negation stripped, or null. */
function fieldTermName(text: string): string | null {
  const m = FIELD_TERM.exec(text.startsWith('-') ? text.slice(1) : text);
  return m ? m[1] : null;
}

/** `grammar::is_field_term` — the parse's shape test, known name or not. It
 *  is what ends an `anything … engineered` scan. */
function isFieldTerm(text: string): boolean {
  return fieldTermName(text) !== null;
}

/** `grammar::is_known_field_term` — a field term on a name the grammar knows. */
function isKnownFieldTerm(text: string): boolean {
  const name = fieldTermName(text);
  if (name === null) return false;
  const lower = asciiLower(name);
  return GRAMMAR_NAMES.has(lower) || COUNTED_HOPS.test(lower);
}

/** ASCII-only lowercasing, as Rust's `eq_ignore_ascii_case`. */
function asciiLower(s: string): string {
  return s.replace(/[A-Z]/g, (c) => c.toLowerCase());
}

/** `grammar::words_at`: these unquoted words, from `at`, ignoring case. */
function wordsAt(tokens: Token[], at: number, words: string[]): boolean {
  return words.every((w, k) => {
    const t = tokens[at + k];
    return t !== undefined && !t.fullyQuoted && asciiLower(t.text) === w;
  });
}

/** Does a lineage phrase start at `tokens[at]`? `grammar::lineage_phrase`
 *  answers `Ok(Some)` or an error for exactly these; either is syntax. */
function lineagePhraseAt(tokens: Token[], at: number): boolean {
  if (wordsAt(tokens, at, ['shares', 'personnel', 'with'])) return true;
  if (wordsAt(tokens, at, ['same', 'pressing', 'family', 'as'])) return true;
  const t = tokens[at];
  if (
    t !== undefined &&
    !t.fullyQuoted &&
    /^[0-9]+$/.test(t.text) &&
    (wordsAt(tokens, at + 1, ['hops', 'from']) || wordsAt(tokens, at + 1, ['hop', 'from']))
  ) {
    return true;
  }
  if (wordsAt(tokens, at, ['anything'])) {
    for (let j = at + 1; j < tokens.length; j++) {
      const w = tokens[j];
      if (!w.fullyQuoted && asciiLower(w.text) === 'engineered') return true;
      if (!w.fullyQuoted && isFieldTerm(w.text)) return false;
    }
  }
  return false;
}

/** `grammar::field_phrase_at`: does a field phrase start at `tokens[at]`? Its
 *  words unquoted and in any case (the first may carry `-`), the last one
 *  followed straight by an operator. */
function fieldPhraseAt(tokens: Token[], at: number): boolean {
  const first = tokens[at];
  if (first === undefined || first.fullyQuoted) return false;
  const firstWord = first.text.startsWith('-') && first.text.length > 1 ? first.text.slice(1) : first.text;
  return GRAMMAR_PHRASES.some((phrase) => {
    const words = phrase.split(' ');
    const n = words.length;
    if (n < 2 || asciiLower(firstWord) !== words[0]) return false;
    if (!wordsAt(tokens, at + 1, words.slice(1, n - 1))) return false;
    const last = tokens[at + n - 1];
    if (last === undefined || last.fullyQuoted) return false;
    const m = FIELD_TERM.exec(last.text);
    return m !== null && asciiLower(m[1]) === words[n - 1];
  });
}

/** `grammar::titled` (TEM-1602 R3b): a field name (or a field phrase's first
 *  word) written with a capital, straight after a capitalised, unquoted word
 *  that is not itself a field term, is part of a title typed as words —
 *  "Village Song: Africa" — and is not syntax. At the start, after a
 *  lower-case word or after a quoted string it still names its field. */
function titled(tokens: Token[], at: number): boolean {
  const prev = at > 0 ? tokens[at - 1] : undefined;
  if (prev === undefined) return false;
  const text = tokens[at].text;
  const word = text.startsWith('-') ? text.slice(1) : text;
  return /^[A-Z]/.test(word) && !prev.fullyQuoted && !isFieldTerm(prev.text) && startsUpper(prev.text);
}

/** Rust's `char::is_uppercase` of the first character: a letter that has a
 *  lower case other than itself. */
function startsUpper(text: string): boolean {
  const first = [...text][0] ?? '';
  return first !== first.toLowerCase();
}

/**
 * Whether `input` uses the grammar's own syntax, as opposed to plain words.
 * Radio sends the first to `GET /ai/query` and the second to its AI search.
 * See the header for why this is a copy, and what holds it honest.
 */
export function hasQuerySyntax(input: string): boolean {
  const tokens = tokenize(input);
  return tokens.some(
    (t, at) =>
      !t.fullyQuoted &&
      (((isKnownFieldTerm(t.text) || fieldPhraseAt(tokens, at)) && !titled(tokens, at)) ||
        (asciiLower(t.text) === 'sounds' && wordsAt(tokens, at + 1, ['like'])) ||
        lineagePhraseAt(tokens, at)),
  );
}
